import { coversUnitShelf, regionsOverlap, type ShelfRegion } from '@cuito/types';
import type { ShelfView as UiShelfView } from '@cuito/ui';
import type { GridShelfView } from '../api/shelves';

/**
 * 坐标底板的行列数区间。
 *
 * `04-api-contract.md:803` 的 `PATCH /locations/:id` 说明「修改名称、**坐标底板行列数**、启用状态」，
 * 区间取自 `05-ui-design-system.md` §2.2.2 R10（行列各 1–20）。前端夹取只是体验：
 * 权威校验在服务端 DTO。
 */
export const SHELF_GRID_MIN = 1;
export const SHELF_GRID_MAX = 20;

export interface GridCell {
  row: number;
  col: number;
}

/** 货架在网格坐标系里的矩形 + 位置标识（几何部分与 `@cuito/types` 的 `ShelfRegion` 同源） */
export interface ShelfBox extends ShelfRegion {
  shelfId: string;
  code: string;
}

export interface MergeSelectionResult {
  /** 规整出的外接矩形；空选择时为 null */
  region: ShelfRegion | null;
  /** 选择是否恰好等于外接矩形（实心） */
  rectangular: boolean;
  error: string | null;
}

/**
 * 把散点选择规整成一个矩形区域。
 *
 * **为什么非矩形必须在客户端拦下**：`04-api-contract.md:995` 明确写了
 * `SHELF_NOT_RECTANGULAR` 不在 `POST /locations/:id/shelves` 的错误表里——
 * 请求体用 `rowStart/colStart/rowSpan/colSpan` 表达，**结构上就只能是矩形**，
 * 服务端既收不到也判断不了 L 形。所以这个判据只能在这里。
 */
export function selectionToRegion(cells: readonly GridCell[]): MergeSelectionResult {
  if (cells.length === 0) {
    return { region: null, rectangular: false, error: '请至少选择一个格子' };
  }

  const rows = cells.map((cell) => cell.row);
  const cols = cells.map((cell) => cell.col);
  const rowStart = Math.min(...rows);
  const colStart = Math.min(...cols);
  const region: ShelfRegion = {
    rowStart,
    colStart,
    rowSpan: Math.max(...rows) - rowStart + 1,
    colSpan: Math.max(...cols) - colStart + 1,
  };

  // 实心判据：选中格数必须等于外接矩形的格数。少了就是缺口（L 形/T 形）。
  if (cells.length !== region.rowSpan * region.colSpan) {
    return { region, rectangular: false, error: '只能选择矩形区域，请补齐缺口或取消多余格子' };
  }

  return { region, rectangular: true, error: null };
}

/**
 * 与外接矩形**几何相交**的已有货架（含「被完整包住」的那些）。
 *
 * 用的是 `@cuito/types` 的 `regionsOverlap`——与后端同一套半开区间比较。
 *
 * **它不是「能不能提交」的判据**：`regionsOverlap` 对包含与被包含同样返回 `true`
 * （`packages/types/src/shelf.spec.ts:31` 就是这么钉的），而契约 §7.2 的副作用
 * （`04-api-contract.md:1077-1081`）明确「新区域完整包住原有 1×1 货架 → 合并迁移并删除原货架（201）」，
 * 只有**交叉**才 409 `SHELF_OVERLAP`。拿这个函数去 `disabled` 按钮，会让
 * 「把散格合成大货架」这个动作永远点不动（合并必然覆盖原有格子）。
 * 阻塞判据是下面的 `planMerge`，本函数只是它的第一层过滤。
 */
export function conflictingShelves(
  region: ShelfRegion,
  shelves: readonly ShelfBox[],
): ShelfBox[] {
  return shelves.filter((shelf) => regionsOverlap(region, shelf));
}

/**
 * 该区域是否**完整包住**了另一个 1×1 货架（契约 §7.2 副作用的几何判据）。
 *
 * **与 `regionsOverlap` 一样来自 `@cuito/types`**（`packages/types/src/shelf.ts`），
 * 本文件不再自己写一份。
 *
 * 这里曾经写的是「服务端那份的镜像」，理由是「两端没有依赖边，各写一次是常规代价」。
 * 那个理由不成立：这条判据是**契约原文的可执行版本**，不是服务端的实现细节，
 * 而 `apps/tenant` **本来就依赖** `@cuito/types`（本文件第 1 行已经在 import
 * `regionsOverlap`）。两份实现被逐条件比对才维持一致，靠的是人工复核 —— 本轮
 * S1（High）「客户端判据与服务端语义不一致 → 功能不可用」正是漏掉那一步的后果。
 * 下沉到契约层后，两边 import 的是同一个函数对象，分叉在构造上不可能发生。
 *
 * （为保持与既有 import 一致，这里转发一次而不是让调用点换路径：
 * `shelfForm.spec.ts` 与 `ShelfMergeSelector.spec.tsx` 的 import 都不用动。
 * 注意必须用「顶层 import + 本地转发」而不是 `export { … } from '@cuito/types'`：
 * 后者**不会**把名字绑进本模块作用域，下面的 `planMerge` 就取不到它。）
 */
export { coversUnitShelf };

/** 一次合并请求对已有货架的影响，按服务端的分流结果分两类 */
export interface MergePlan {
  /** 会让服务端回 409 `SHELF_OVERLAP` 的货架——提交必须被拦下 */
  blocking: ShelfBox[];
  /** 会被服务端合并迁移（`ShelfItem`/`ShelfIcon` 保留）后删除的 1×1 货架——只提示，不阻塞 */
  absorbed: ShelfBox[];
}

/**
 * 客户端镜像服务端的「吞并 vs 冲突」分流（契约 §7.2 副作用的可执行版本）。
 *
 * 服务端的权威实现是 `apps/api/src/modules/shelves/shelves.service.ts:432-438`
 * （`coversUnitShelf` → `absorbed`，其余 → `blocking`，有 `blocking` 才 409）。
 * 这里必须与它同口径，否则会出现两种坏结果，而且**两种都不可见**：
 *   · 判严了（把包含当冲突）→ 合并功能整体不可用，用户只能先删货架再建，
 *     连带丢掉格子里的 `ShelfItem` 与 `ShelfIcon`（契约明说这些应当迁移保留）；
 *   · 判松了（把交叉当合并）→ 按钮放行一个必然 409 的请求，用户白填一遍表单。
 * 服务端仍是最终权威（并发创建时前端看不到别人的新货架），这里只是把
 * 「提交前就能确定会失败」的那一半拦下来。
 */
export function planMerge(region: ShelfRegion, shelves: readonly ShelfBox[]): MergePlan {
  const blocking: ShelfBox[] = [];
  const absorbed: ShelfBox[] = [];

  for (const shelf of conflictingShelves(region, shelves)) {
    if (coversUnitShelf(region, shelf)) absorbed.push(shelf);
    else blocking.push(shelf);
  }

  return { blocking, absorbed };
}

/** 阻塞提交的货架提示：哪几个货架挡住了，直接由其位置标识点名 */
export function describeBlockingShelves(blocking: readonly ShelfBox[]): string {
  return `与已有货架重叠：${blocking.map((shelf) => shelf.code).join('、')}`;
}

/**
 * 会被合并掉的 1×1 货架提示。
 *
 * 必须**说出来**而不是静默吞并：这些货架被删除是副作用里唯一会让用户惊讶的部分，
 * 而「商品与图标保留」正是让人敢按下确认的那句话。
 */
export function describeAbsorbedShelves(absorbed: readonly ShelfBox[]): string {
  return `将合并 ${absorbed.length} 个 1×1 货架（商品与图标保留）：${absorbed
    .map((shelf) => shelf.code)
    .join('、')}`;
}

/** 把坐标底板缩到 `rows × cols` 后，哪些货架会越界（服务端会回 400 `SHELF_OUT_OF_GRID`） */
export function shelvesOutOfGrid(
  shelves: readonly ShelfBox[],
  rows: number,
  cols: number,
): ShelfBox[] {
  return shelves.filter(
    (shelf) =>
      shelf.rowStart + shelf.rowSpan - 1 > rows || shelf.colStart + shelf.colSpan - 1 > cols,
  );
}

/** 行列数输入框的夹取：非数字兜底成下限，小数截断（DTO 是 `@IsInt()`） */
export function clampGridSize(value: number): number {
  if (!Number.isFinite(value)) return SHELF_GRID_MIN;
  return Math.min(SHELF_GRID_MAX, Math.max(SHELF_GRID_MIN, Math.trunc(value)));
}

/**
 * 把 `409 SHELF_OVERLAP` 的 `details.conflicts` 变成一句人话。
 *
 * 契约只说「`details.conflicts` 列出冲突货架」（`04-api-contract.md:991`），
 * **没有定义元素的形状**，所以字符串与对象两种都容错；拿不到就退回通用文案，
 * 绝不显示 `undefined`。
 */
export function describeConflicts(details: unknown): string {
  const conflicts =
    details && typeof details === 'object'
      ? (details as { conflicts?: unknown }).conflicts
      : undefined;

  if (!Array.isArray(conflicts) || conflicts.length === 0) return '与其他货架区域重叠';

  const labels = conflicts.map((entry) => {
    if (typeof entry === 'string') return entry;
    if (entry && typeof entry === 'object') {
      const candidate = entry as { code?: unknown; label?: unknown; shelfId?: unknown };
      if (typeof candidate.code === 'string') {
        return typeof candidate.label === 'string' && candidate.label !== ''
          ? `${candidate.code}（${candidate.label}）`
          : candidate.code;
      }
      if (typeof candidate.shelfId === 'string') return candidate.shelfId;
    }
    return '未知货架';
  });

  return `与已有货架重叠：${labels.join('、')}`;
}

/**
 * 网格响应 → `packages/ui` 的 `ShelfView`。
 *
 * 派生字段只在这一处计算：`productCount` 与 `firstProductImage` 若由组件各算一次，
 * 迟早与「items 已按 sortOrder 升序」这条服务端保证脱节。
 */
export function toUiShelf(shelf: GridShelfView): UiShelfView {
  return {
    shelfId: shelf.shelfId,
    code: shelf.code,
    label: shelf.label,
    rowStart: shelf.rowStart,
    colStart: shelf.colStart,
    rowSpan: shelf.rowSpan,
    colSpan: shelf.colSpan,
    iconUrl: shelf.iconUrl,
    soldOut: shelf.soldOut,
    productCount: shelf.items.length,
    firstProductImage: shelf.items[0]?.imageUrl ?? null,
  };
}
